import { LockOutlined } from '@ant-design/icons'
import { Empty, Spin, Tag, Tree } from 'antd'
import { useMemo } from 'react'

import type { PermissionScopeInfo } from '../hooks/usePermissionScopes'
import { mergeCheckedScopes, scopesOutsideCatalog } from '../scopeCatalog'
import { UnknownScopes } from './UnknownScopes'

interface Props {
  scopes: PermissionScopeInfo[]
  value: string[]
  onChange: (value: string[]) => void
  callerPermissions: readonly string[]
  loading?: boolean
  disabled?: boolean
}

function groupOf(scope: string): string {
  return scope.split(':')[0] || 'other'
}

export function canGrantPermission(
  callerPermissions: readonly string[],
  scope: string,
): boolean {
  return (
    callerPermissions.includes('admin') || callerPermissions.includes(scope)
  )
}

export function getUngrantedPermissions(
  selected: readonly string[],
  callerPermissions: readonly string[],
): string[] {
  return selected.filter(
    (scope) => !canGrantPermission(callerPermissions, scope),
  )
}

/** Checkable tree of permission scopes grouped by prefix, admin groups flagged. */
export function PermissionPicker({
  scopes,
  value,
  onChange,
  callerPermissions,
  loading,
  disabled,
}: Props) {
  const { treeData } = useMemo(() => {
    const groups = new Map<string, PermissionScopeInfo[]>()
    for (const info of scopes) {
      const group = groupOf(info.scope)
      const list = groups.get(group) ?? []
      list.push(info)
      groups.set(group, list)
    }

    const treeData = [...groups.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([group, items]) => ({
        key: `group:${group}`,
        title: (
          <span>
            {group}
            {items.some((i) => i.isAdmin) && (
              <Tag
                color="volcano"
                icon={<LockOutlined />}
                style={{ marginLeft: 8 }}
              >
                admin
              </Tag>
            )}
            <span style={{ color: '#999', marginLeft: 8 }}>
              ({items.length})
            </span>
          </span>
        ),
        children: items
          .slice()
          .sort((a, b) => a.scope.localeCompare(b.scope))
          .map((i) => ({
            key: i.scope,
            title: i.scope,
            disabled:
              disabled ||
              (!value.includes(i.scope) &&
                !canGrantPermission(callerPermissions, i.scope)),
          })),
      }))

    return { treeData }
  }, [callerPermissions, disabled, scopes, value])

  if (loading) return <Spin />

  const catalog: string[] = scopes.map(({ scope }) => scope)
  let catalogContent = <Empty description="No permission scopes" />

  if (scopes.length) {
    catalogContent = (
      <Tree
        checkable
        disabled={disabled}
        selectable={false}
        treeData={treeData}
        checkedKeys={value}
        onCheck={(checked) => {
          const keys = Array.isArray(checked) ? checked : checked.checked
          onChange(mergeCheckedScopes(value, keys.map(String), catalog))
        }}
      />
    )
  }

  return (
    <>
      {catalogContent}
      <UnknownScopes
        scopes={scopesOutsideCatalog(value, catalog)}
        disabled={disabled}
        onRemove={(scope) => onChange(value.filter((item) => item !== scope))}
      />
    </>
  )
}
